import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Hooks
import { usePreventPullToRefresh } from '../../hooks/usePreventPullToRefresh';
import { useParentWrapperSwipe } from '../../hooks/useParentWrapperSwipe';
import * as urls from '../../utils/urls';
import styles from './style.module.scss';

type Answer = { id: number; answer: string; is_correct: boolean };
type Question = {
  id: number;
  body: string;
  type: string;
  answers?: Answer[];
  correct_answer?: string;
};

type Game2048Props = {
  quizId: number;
  questions: Question[];
};

type Direction = 'left' | 'right' | 'up' | 'down';
type GameStatus = 'playing' | 'won' | 'lost' | 'finished';
type QuestionResult = 'correct' | 'incorrect' | null;

const GRID_SIZE = 4;
const TARGET_TILE = 2048;
const MOVES_PER_QUESTION = 10;
const QUESTION_FEEDBACK_DELAY = 1400;

// Question generation constants
const QUESTIONS_POLL_INTERVAL_MS = 2000;
const QUESTIONS_GENERATION_TIMEOUT_MS = 60000;
const QUESTIONS_GENERATION_DEBOUNCE_MS = 5000;
const QUESTIONS_TRIGGER_THRESHOLD = 4; // Trigger generation when 4 questions remain

const directionMap: Record<string, Direction> = {
  ArrowLeft: 'left',
  ArrowRight: 'right',
  ArrowUp: 'up',
  ArrowDown: 'down',
  a: 'left',
  A: 'left',
  d: 'right',
  D: 'right',
  w: 'up',
  W: 'up',
  s: 'down',
  S: 'down',
};

const createEmptyGrid = (): number[][] => Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(0));

const randomTileValue = () => (Math.random() < 0.9 ? 2 : 4);

const cloneBoard = (board: number[][]): number[][] => board.map(row => [...row]);

const getAvailableCells = (board: number[][]): Array<[number, number]> => {
  const cells: Array<[number, number]> = [];
  board.forEach((row, rowIndex) => {
    row.forEach((value, colIndex) => {
      if (value === 0) {
        cells.push([rowIndex, colIndex]);
      }
    });
  });
  return cells;
};

const addRandomTile = (board: number[][]): { board: number[][]; newTilePosition: string | null } => {
  const availableCells = getAvailableCells(board);
  if (availableCells.length === 0) {
    return { board, newTilePosition: null };
  }
  const [row, col] = availableCells[Math.floor(Math.random() * availableCells.length)];
  const next = cloneBoard(board);
  next[row][col] = randomTileValue();
  return { board: next, newTilePosition: `${row}-${col}` };
};

const createStartingGrid = (): number[][] => {
  const first = addRandomTile(createEmptyGrid());
  const second = addRandomTile(first.board);
  return second.board;
};

const rotateClockwise = (board: number[][]): number[][] => {
  const size = board.length;
  const rotated = createEmptyGrid();
  for (let row = 0; row < size; row += 1) {
    for (let col = 0; col < size; col += 1) {
      rotated[col][size - 1 - row] = board[row][col];
    }
  }
  return rotated;
};

type MoveResult = {
  board: number[][];
  moved: boolean;
  gained: number;
  mergedPositions: Set<string>;
};

const shiftLeft = (board: number[][]): MoveResult => {
  let moved = false;
  let gained = 0;
  const mergedPositions = new Set<string>();
  
  const next = board.map((row, rowIndex) => {
    const numbers = row.filter(value => value !== 0);
    const merged: number[] = [];
    let skip = false;
    for (let index = 0; index < numbers.length; index += 1) {
      if (skip) {
        skip = false;
        continue;
      }
      const current = numbers[index];
      const neighbour = numbers[index + 1];
      if (neighbour && current === neighbour) {
        const mergedValue = current * 2;
        merged.push(mergedValue);
        mergedPositions.add(`${rowIndex}-${merged.length - 1}`);
        gained += mergedValue;
        skip = true;
      } else {
        merged.push(current);
      }
    }
    while (merged.length < GRID_SIZE) {
      merged.push(0);
    }
    if (!moved && merged.some((value, cellIndex) => value !== row[cellIndex])) {
      moved = true;
    }
    return merged;
  });

  return { board: next, moved, gained, mergedPositions };
};

const shiftTiles = (board: number[][], direction: Direction): MoveResult => {
  let working = cloneBoard(board);
  let rotations = 0;
  if (direction === 'up') {
    rotations = 3;
  } else if (direction === 'right') {
    rotations = 2;
  } else if (direction === 'down') {
    rotations = 1;
  }

  for (let step = 0; step < rotations; step += 1) {
    working = rotateClockwise(working);
  }

  const result = shiftLeft(working);

  let restored = result.board;
  const rotateBackTimes = (4 - rotations) % 4;
  for (let step = 0; step < rotateBackTimes; step += 1) {
    restored = rotateClockwise(restored);
  }

  const transformedMergedPositions = new Set<string>();
  result.mergedPositions.forEach(pos => {
    const [row, col] = pos.split('-').map(Number);
    let finalRow = row;
    let finalCol = col;
    
    for (let step = 0; step < rotateBackTimes; step += 1) {
      const temp = finalRow;
      finalRow = finalCol;
      finalCol = GRID_SIZE - 1 - temp;
    }
    
    transformedMergedPositions.add(`${finalRow}-${finalCol}`);
  });

  return { board: restored, moved: result.moved, gained: result.gained, mergedPositions: transformedMergedPositions };
};

const hasAvailableMoves = (board: number[][]): boolean => {
  for (let row = 0; row < GRID_SIZE; row += 1) {
    for (let col = 0; col < GRID_SIZE; col += 1) {
      const value = board[row][col];
      if (value === 0) {
        return true;
      }
      if (col < GRID_SIZE - 1 && value === board[row][col + 1]) {
        return true;
      }
      if (row < GRID_SIZE - 1 && value === board[row + 1][col]) {
        return true;
      }
    }
  }
  return false;
};

const containsTargetTile = (board: number[][]): boolean =>
  board.some(row => row.some(value => value >= TARGET_TILE));

const getCsrfToken = (): string => {
  const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
  return token ?? '';
};

const Game2048: React.FC<Game2048Props> = ({ quizId, questions: initialQuestions }) => {
  const { t } = useTranslation();
  usePreventPullToRefresh();

  const [grid, setGrid] = useState<number[][]>(createStartingGrid);
  const [score, setScore] = useState(0);
  const [moves, setMoves] = useState(0);
  const [gameStatus, setGameStatus] = useState<GameStatus>('playing');
  const [showQuestion, setShowQuestion] = useState(false);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [freeResponseValue, setFreeResponseValue] = useState('');
  const [freeResponseSubmittedValue, setFreeResponseSubmittedValue] = useState('');
  const [questionResult, setQuestionResult] = useState<QuestionResult>(null);
  const [selectedAnswerId, setSelectedAnswerId] = useState<number | null>(null);
  const [sessionId, setSessionId] = useState<string>(() => crypto.randomUUID());
  const [startTime, setStartTime] = useState<number>(() => Date.now());
  const [finished, setFinished] = useState(false);
  const [mergedTiles, setMergedTiles] = useState<Set<string>>(new Set());
  const [newTile, setNewTile] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [questions, setQuestions] = useState<Question[]>(initialQuestions);
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);
  const [lastGeneratedAt, setLastGeneratedAt] = useState<number>(0);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const totalQuestions = questions.length;
  const allQuestionsAnswered = useMemo(() => {
    return totalQuestions > 0 && questionIndex >= totalQuestions;
  }, [questionIndex, totalQuestions]);

  const currentQuestion = useMemo(() => {
    if (totalQuestions === 0) {
      return undefined;
    }
    return questions[questionIndex] ?? undefined;
  }, [questions, questionIndex, totalQuestions]);

  const correctAnswerIds = useMemo(() => {
    if (!currentQuestion?.answers) {
      return new Set<number>();
    }
    return new Set(currentQuestion.answers.filter(answer => answer.is_correct).map(answer => answer.id));
  }, [currentQuestion]);

  const correctAnswerText = useMemo(() => {
    if (!currentQuestion?.answers) {
      return '';
    }
    return currentQuestion.answers
      .filter(answer => answer.is_correct)
      .map(answer => answer.answer)
      .join(', ');
  }, [currentQuestion]);

  const resultsPath = useMemo(() => {
    if (!sessionId) {
      return null;
    }
    return `/quizzes/${quizId}/results?session_id=${sessionId}&start_time=${startTime}`;
  }, [quizId, sessionId, startTime]);

  const openQuestionModal = useCallback(() => {
    if (totalQuestions === 0) {
      return;
    }
    if (questionIndex >= totalQuestions) {
      return;
    }
    setShowQuestion(true);
    setQuestionResult(null);
    setSelectedAnswerId(null);
    setFreeResponseValue('');
    setFreeResponseSubmittedValue('');
  }, [questionIndex, totalQuestions]);

  const resetQuestionState = useCallback(() => {
    setShowQuestion(false);
    setQuestionResult(null);
    setSelectedAnswerId(null);
    setFreeResponseValue('');
    setFreeResponseSubmittedValue('');
  }, []);

  const startNewGame = useCallback(() => {
    setGrid(createStartingGrid());
    setScore(0);
    setMoves(0);
    setGameStatus('playing');
    setShowQuestion(false);
    setQuestionResult(null);
    setQuestionIndex(0);
    setFreeResponseValue('');
    setFreeResponseSubmittedValue('');
    setSelectedAnswerId(null);
    setSessionId(crypto.randomUUID());
    setStartTime(Date.now());
    setFinished(false);
    setMergedTiles(new Set());
    setNewTile(null);
  }, []);

  const finishQuiz = useCallback(() => {
    if (finished || !resultsPath) {
      return;
    }

    setFinished(true);
    setGameStatus('finished');

    const turbo = (window as typeof window & { Turbo?: { visit?: (path: string, options?: Record<string, unknown>) => void } }).Turbo;
    if (turbo?.visit) {
      turbo.visit(resultsPath, { action: 'replace' });
      return;
    }

    Turbo.visit(resultsPath);
  }, [finished, resultsPath]);

  const handleMove = useCallback(
    (direction: Direction) => {
      if (gameStatus !== 'playing' || showQuestion || finished) {
        return;
      }

      setGrid(prevGrid => {
        const result = shiftTiles(prevGrid, direction);
        if (!result.moved) {
          return prevGrid;
        }

        const { board: boardWithNewTile, newTilePosition } = addRandomTile(result.board);
        const reachedTarget = containsTargetTile(boardWithNewTile);
        const canContinue = hasAvailableMoves(boardWithNewTile);
        setMergedTiles(result.mergedPositions);
        setNewTile(newTilePosition);

        setScore(prevScore => {
          return prevScore + result.gained;
        });

        setMoves(prevMoves => {
          const totalMoves = prevMoves + 1;
          if (totalMoves % MOVES_PER_QUESTION === 0 && !allQuestionsAnswered && questionIndex < totalQuestions) {
            openQuestionModal();
          }
          return totalMoves;
        });

        if (reachedTarget) {
          setGameStatus('won');
        } else if (!canContinue) {
          setGameStatus('lost');
        }

        return boardWithNewTile;
      });
    },
    [finished, gameStatus, questionIndex, showQuestion, totalQuestions, openQuestionModal, allQuestionsAnswered]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const direction = directionMap[event.key];
      if (!direction) {
        return;
      }
      event.preventDefault();
      handleMove(direction);
    },
    [handleMove]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown, { passive: false });
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (mergedTiles.size === 0 && newTile === null) {
      return;
    }

    const timer = window.setTimeout(() => {
      setMergedTiles(new Set());
      setNewTile(null);
    }, 200);

    return () => window.clearTimeout(timer);
  }, [mergedTiles, newTile]);


  const handleSwipe = useCallback(
    (deltaX: number, deltaY: number, direction: 'up' | 'down' | 'left' | 'right') => {
      handleMove(direction);
    },
    [handleMove]
  );

  useParentWrapperSwipe({
    containerRef,
    onSwipe: handleSwipe,
    enabled: gameStatus === 'playing' && !showQuestion && !finished,
  });

  const submitQuestionResult = useCallback(
    async (question: Question | undefined, answerValue: string, isCorrect: boolean) => {
      if (!question || !sessionId) {
        return;
      }

      try {
        const response = await fetch(urls.questionResultUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCsrfToken()
          },
          body: JSON.stringify({
            question_result: {
              question_id: question.id,
              answer: answerValue,
              correct: isCorrect
            },
            session_id: sessionId
          })
        });

        if (!response.ok) {
          console.error('Failed to submit question result');
        }
      } catch (error) {
        console.error('Error submitting question result', error);
      }
    },
    [sessionId]
  );

  const handleMultipleChoiceSelect = useCallback(
    (answer: Answer) => {
      if (!currentQuestion || questionResult !== null) {
        return;
      }

      const isCorrect = Boolean(answer.is_correct);
      setSelectedAnswerId(answer.id);
      setQuestionResult(isCorrect ? 'correct' : 'incorrect');
      submitQuestionResult(currentQuestion, answer.answer, isCorrect);
    },
    [currentQuestion, questionResult, submitQuestionResult]
  );

  const handleSubmitFreeResponse = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!currentQuestion || questionResult !== null) {
        return;
      }
      const expected = currentQuestion.correct_answer?.trim().toLowerCase();
      const provided = freeResponseValue.trim();
      const normalizedProvided = provided.toLowerCase();

      const hasExpectedAnswer = Boolean(expected && expected.length > 0);
      const isCorrect = hasExpectedAnswer && normalizedProvided !== '' && normalizedProvided === expected;

      setFreeResponseSubmittedValue(provided);
      setFreeResponseValue(provided);
      setQuestionResult(isCorrect ? 'correct' : 'incorrect');
      submitQuestionResult(currentQuestion, provided, isCorrect);
    },
    [currentQuestion, freeResponseValue, questionResult, submitQuestionResult]
  );

  const cleanupPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    setIsGeneratingQuestions(false);
  }, []);

  const generateMoreQuestions = useCallback(async () => {
    if (isGeneratingQuestions) {
      return;
    }

    setIsGeneratingQuestions(true);
    const currentQuestionCount = questions.length;
    
    try {
      const response = await fetch(urls.aiGameGenerateMoreQuestionsUrl(quizId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        }
      });

      if (!response.ok) {
        console.error('Failed to trigger question generation');
        setIsGeneratingQuestions(false);
        return;
      }

      pollIntervalRef.current = setInterval(async () => {
        try {
          const questionsResponse = await fetch(urls.aiGameQuestionsUrl(quizId), {
            headers: {
              'X-CSRF-Token': getCsrfToken()
            }
          });

          if (!questionsResponse.ok) {
            return;
          }

          const data = await questionsResponse.json();
          const newQuestions = data.questions || [];
          
          if (newQuestions.length > currentQuestionCount) {
            setQuestions(newQuestions);
            cleanupPolling();
          }
        } catch (error) {
          console.error('Error polling for questions', error);
          cleanupPolling();
        }
      }, QUESTIONS_POLL_INTERVAL_MS);

      pollTimeoutRef.current = setTimeout(() => {
        cleanupPolling();
      }, QUESTIONS_GENERATION_TIMEOUT_MS);
    } catch (error) {
      console.error('Error initiating question generation', error);
      setIsGeneratingQuestions(false);
    }
  }, [quizId, questions.length, isGeneratingQuestions, cleanupPolling]);

  const shouldTriggerGeneration = useCallback((currentIndex: number, currentTotal: number): boolean => {
    return currentIndex === currentTotal - QUESTIONS_TRIGGER_THRESHOLD && currentTotal > QUESTIONS_TRIGGER_THRESHOLD;
  }, []);

  useEffect(() => {
    if (questionResult === null) {
      return;
    }

    const timer = window.setTimeout(() => {
      const isLastQuestion = totalQuestions === 0 || questionIndex >= totalQuestions - 1;
      resetQuestionState();

      if (!isLastQuestion) {
        const newIndex = questionIndex + 1;
        setQuestionIndex(newIndex);
        
        if (shouldTriggerGeneration(newIndex, totalQuestions) && !isGeneratingQuestions) {
          const timeSinceLastGeneration = Date.now() - lastGeneratedAt;
          if (timeSinceLastGeneration > QUESTIONS_GENERATION_DEBOUNCE_MS) {
            setLastGeneratedAt(Date.now());
            generateMoreQuestions();
          }
        }
      } else {
        setQuestionIndex(totalQuestions);
      }
    }, QUESTION_FEEDBACK_DELAY);

    return () => window.clearTimeout(timer);
  }, [questionIndex, questionResult, resetQuestionState, totalQuestions, shouldTriggerGeneration, generateMoreQuestions, isGeneratingQuestions, lastGeneratedAt]);

  useEffect(() => {
    if (finished || (gameStatus !== 'won' && gameStatus !== 'lost')) {
      return;
    }

    if (showQuestion) {
      resetQuestionState();
    }

    const timer = window.setTimeout(() => {
      finishQuiz();
    }, 300);

    return () => window.clearTimeout(timer);
  }, [finished, gameStatus, showQuestion, resetQuestionState, finishQuiz]);

  useEffect(() => {
    setQuestions(initialQuestions);
  }, [initialQuestions]);

  useEffect(() => {
    return () => {
      cleanupPolling();
    };
  }, [cleanupPolling]);

  const getTileClassName = useCallback(
    (value: number, rowIndex: number, colIndex: number) => {
      if (value === 0) {
        return `${styles.tile} ${styles.tileEmpty}`;
      }
      
      const position = `${rowIndex}-${colIndex}`;
      const capped = value <= 2048 ? value : 'Super';
      const colorClass = styles[`tile${capped}` as keyof typeof styles] ?? styles.tileSuper;
      const sizeClass =
        value >= 1000 ? styles.tileTextSmall : value >= 128 ? styles.tileTextMedium : undefined;
      
      const isMerged = mergedTiles.has(position);
      const isNew = newTile === position;
      const animationClass = isMerged ? styles.tileMerged : isNew ? styles.tileNew : '';
      
      return `${styles.tile} ${colorClass}${sizeClass ? ` ${sizeClass}` : ''}${animationClass ? ` ${animationClass}` : ''}`;
    },
    [mergedTiles, newTile]
  );

  const isGameOver = gameStatus === 'lost';

  const questionAnswerInputId = currentQuestion ? `game-2048-answer-${currentQuestion.id}` : 'game-2048-answer';
  const shouldShowFeedback = questionResult !== null;

  return (
    <div 
      ref={containerRef}
      className={`flex flex-col gap-4 w-full max-w-md mx-auto ${styles.container}`}
      style={{ overscrollBehaviorY: 'none' }}
    >
      <div className="flex items-stretch justify-end gap-2 md:gap-4">
        <button
          type="button"
          className={`flex items-center justify-center rounded-xl px-4 md:px-6 font-semibold tracking-[0.02em] ${styles.newGameButton}`}
          onClick={startNewGame}
        >
          {t('games.game2048.new_game')}
        </button>
        <div className={`flex items-center justify-center gap-1 min-h-[56px] px-4 py-2 rounded-xl font-semibold flex-col min-w-[112px] ${styles.scorePill}`}>
          <span className="block text-xs opacity-80 uppercase font-semibold">{t('games.game2048.score')}</span>
          <span className="block text-xl font-semibold">{score}</span>
        </div>
        <div className={`flex items-center justify-center gap-1 min-h-[56px] px-4 py-2 rounded-xl font-semibold flex-col min-w-[112px] ${styles.metaPill}`}>
          <span className="block text-xs opacity-80 uppercase font-semibold">{t('games.game2048.moves')}</span>
          <span className="block text-xl font-semibold">{moves}</span>
        </div>
      </div>
      <div
        className={`grid p-3 md:p-4 gap-2 md:gap-3 rounded-2xl relative ${styles.board}`}
        role="application"
        aria-label={t('games.game2048.title')}
      >
        {grid.map((row, rowIndex) =>
          row.map((value, colIndex) => (
            <div key={`${rowIndex}-${colIndex}`} className={getTileClassName(value, rowIndex, colIndex)}>
              {value !== 0 ? value : ''}
            </div>
          ))
        )}

        {isGameOver && (
          <div className={`absolute inset-0 flex items-center justify-center rounded-2xl pointer-events-none ${styles.gameOverOverlay}`}>
            <div className={`flex flex-col items-center justify-center gap-4 pointer-events-auto ${styles.gameOverContent}`}>
              <h3 className={`text-center text-lg font-semibold m-0 ${styles.gameOverTitle}`}>{t('games.game2048.messages.game_over')}</h3>
              <button type="button" className={styles.gameOverButton} onClick={startNewGame}>
                {t('games.game2048.messages.try_again')}
              </button>
            </div>
          </div>
        )}
      </div>

      {showQuestion && currentQuestion && (
        <div className={`fixed inset-0 flex items-center justify-center px-3 py-0 ${styles.overlay}`}>
          <div className={`flex flex-col gap-4 p-5 rounded-2xl ${styles.overlayContent}`}>
            <h3 className={styles.overlayTitle}>{t('games.game2048.question_modal.title')}</h3>
            <p className={styles.overlayMessage} dir="auto">{currentQuestion.body}</p>

            {currentQuestion.answers && currentQuestion.answers.length > 0 ? (
              <div className={styles.answersWrapper}>
                <p className={styles.questionHint}>
                  {t('games.game2048.question_modal.multiple_choice_label')}
                </p>
                <div className={styles.answersList}>
                  {currentQuestion.answers.map(answer => {
                    const isCorrectAnswer = correctAnswerIds.has(answer.id);
                    const isSelected = selectedAnswerId === answer.id;
                    let buttonClass = styles.answerButton;

                    if (shouldShowFeedback) {
                      buttonClass = `${buttonClass} ${styles.answerButtonDisabled}`;
                      if (isCorrectAnswer) {
                        buttonClass = `${buttonClass} ${styles.answerButtonCorrect}`;
                      }
                      if (isSelected && !isCorrectAnswer) {
                        buttonClass = `${buttonClass} ${styles.answerButtonIncorrect}`;
                      }
                    }

                    return (
                      <button
                        key={answer.id}
                        type="button"
                        className={buttonClass}
                        onClick={() => handleMultipleChoiceSelect(answer)}
                        disabled={shouldShowFeedback}
                        dir="auto"
                      >
                        {answer.answer}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <form className={styles.freeResponse} onSubmit={handleSubmitFreeResponse}>
                <label htmlFor={questionAnswerInputId} className={styles.questionHint}>
                  {t('games.game2048.question_modal.free_response_label')}
                </label>
                <input
                  id={questionAnswerInputId}
                  value={freeResponseValue}
                  onChange={event => setFreeResponseValue(event.target.value)}
                  placeholder={t('games.game2048.question_modal.free_response_placeholder') ?? ''}
                  className={styles.input}
                  autoFocus
                  disabled={shouldShowFeedback}
                />
                <button
                  type="submit"
                  className={styles.primaryButton}
                  disabled={shouldShowFeedback || freeResponseValue.trim() === ''}
                >
                  {t('actions.submit')}
                </button>
              </form>
            )}

            {shouldShowFeedback && (
              <div className={styles.feedbackMessage}>
                <p
                  className={`${styles.questionResultMessage} ${questionResult === 'correct' ? styles.questionResultCorrect : styles.questionResultIncorrect}`}
                >
                  {questionResult === 'correct'
                    ? t('games.game2048.question_modal.correct')
                    : t('games.game2048.question_modal.incorrect')}
                </p>

                {questionResult === 'incorrect' && (
                  <>
                    {currentQuestion.answers && currentQuestion.answers.length > 0 && correctAnswerText && (
                      <p className={styles.correctAnswerReveal}>
                        <span className={styles.correctAnswerLabel}>{t('globals.correct_answer')}:</span>
                        <span className={styles.correctAnswerValue} dir="auto">{correctAnswerText}</span>
                      </p>
                    )}
                    {(!currentQuestion.answers || currentQuestion.answers.length === 0) && currentQuestion.correct_answer && (
                      <p className={styles.correctAnswerReveal}>
                        <span className={styles.correctAnswerLabel}>{t('globals.correct_answer')}:</span>
                        <span className={styles.correctAnswerValue} dir="auto">{currentQuestion.correct_answer}</span>
                      </p>
                    )}
                  </>
                )}

                {questionResult === 'incorrect' && freeResponseSubmittedValue && (
                  <p className={styles.userAnswerReveal}>
                    <span className={styles.correctAnswerLabel}>{t('globals.incorrect')}:</span>
                    <span className={styles.correctAnswerValue} dir="auto">{freeResponseSubmittedValue}</span>
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Game2048;


